import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";

export interface SubmissionSuccessProps {
  /**
   * Already-translated heading. Each surface keeps its own wording. Omit where
   * the surrounding chrome already announces it — QuoteRequestDialog puts both
   * in its DialogHeader, and repeating them here would have a screen reader
   * read the confirmation twice.
   */
  title?: string;
  /** Already-translated body. Omit under the same rule as `title`. */
  body?: string;
  /** `VQ-1042`, or null when Odoo returned no id — see `quote-reference.ts`. */
  reference?: string | null;
  /** Renders a "send another" control when the surface can reset its form. */
  onReset?: () => void;
  /** Extra content between the body and the reference — e.g. a next-steps list. */
  children?: ReactNode;
  className?: string;
}

/**
 * In-container confirmation for a submitted lead.
 *
 * Replaces the toast-only pattern that five of the six quote surfaces used
 * (VARS-056). A `sonner` toast auto-dismisses and renders at the top of the
 * viewport — away from the form the user was looking at — so on a long page the
 * only confirmation a submission produced could disappear before it was read,
 * or never enter the viewport at all. This renders where the form was, persists,
 * and carries the reference number the API was already returning and every
 * caller was discarding.
 *
 * `role="status"` (not `alert`) so assistive tech announces it politely on the
 * success path; the heading takes focus via `tabIndex={-1}` at the call site
 * where the form is unmounted, so keyboard focus is not orphaned on a removed
 * element.
 */
export function SubmissionSuccess({
  title,
  body,
  reference,
  onReset,
  children,
  className,
}: SubmissionSuccessProps) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!reference) return;
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable over plain http and in some embedded
      // webviews. The reference is selectable text either way, so failing
      // silently leaves the user no worse off than having no button.
    }
  };

  return (
    <div
      role="status"
      className={cn("py-12 text-center space-y-6", className)}
      data-testid="submission-success"
    >
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-mint/20 text-mint-ink">
        <CheckCircle2 className="h-10 w-10" aria-hidden="true" />
      </div>

      {title || body ? (
        <div className="space-y-2">
          {title ? (
            <h2 tabIndex={-1} className="font-display text-3xl font-bold text-navy outline-none">
              {title}
            </h2>
          ) : null}
          {body ? (
            <p className="mx-auto max-w-md text-sm text-muted-foreground leading-relaxed">{body}</p>
          ) : null}
        </div>
      ) : null}

      {children}

      {reference ? (
        <div className="mx-auto max-w-sm rounded-2xl border border-border/80 bg-surface-alt p-4 space-y-2">
          <div className="text-xs font-bold text-navy">{t("form.success.referenceLabel")}</div>
          <div className="flex items-center justify-center gap-2">
            {/* `select-all` so it can be grabbed by hand wherever the
                clipboard API is unavailable. */}
            <span className="select-all font-mono text-lg font-bold tracking-wide text-navy">
              {reference}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={copy}
              aria-label={copied ? t("form.success.copied") : t("form.success.copy")}
              className="h-8 w-8 p-0"
            >
              {copied ? (
                <Check className="h-4 w-4 text-mint-ink" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {t("form.success.referenceHint")}
          </p>
          <span aria-live="polite" className="sr-only">
            {copied ? t("form.success.copied") : ""}
          </span>
          <div className="pt-2 border-t border-border/50">
            <Link
              to="/account/quotes"
              className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1"
            >
              {t("account.quotes.trackOnline")} →
            </Link>
          </div>
        </div>
      ) : null}

      {onReset ? (
        <Button type="button" variant="outline" onClick={onReset}>
          {t("form.success.again")}
        </Button>
      ) : null}
    </div>
  );
}
